import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getManualReviews } from '../services/api';
import ReviewCard from '../components/ReviewCard';
import styles from './ReviewFeed.module.scss';
import ConfirmModal from '../components/ConfirmModal';

interface Review {
  _id: string;
  author: string;
  content: string;
  rating: number;
  date?: string;
  platform?: string;
  response?: string;
}

const ReviewFeed: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Single source of truth for your API base:
  const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    getManualReviews()
      .then(setReviews)
      .catch(() => setError("Erreur lors du chargement des avis"))
      .finally(() => setLoading(false));
  }, []);

  const openReplyEditor = (review: Review) => {
    setReplyingId(review._id);
    setReply(review.response || '');
  };

  const sendReply = async (reviewId: string) => {
    try {
      const res = await fetch(`${API}/reviews/${reviewId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ response: reply }),
      });

      if (res.ok) {
        toast.success('✅ Réponse envoyée avec succès !');
        setReply('');
        setReplyingId(null);
        const updated = await getManualReviews();
        setReviews(updated);
      } else {
        toast.error("❌ Erreur lors de l'envoi. Veuillez réessayer.");
      }
    } catch (err) {
      console.error('Send reply error:', err);
      toast.error('Erreur réseau.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;

    try {
      const res = await fetch(`${API}/reviews/${confirmDeleteId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (res.ok) {
        toast.success('Avis supprimé.');
        const updated = await getManualReviews();
        setReviews(updated);
      } else {
        toast.error('Erreur lors de la suppression.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur réseau.');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const generateAIReply = async (review: Review) => {
    try {
      setAiLoading(true);
      const res = await fetch(`${API}/ai/generate-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          text: review.content,
          tone: 'friendly',
        }),
      });

      // If AI is disabled on the server, this may return 503/HTML
      if (!res.ok) {
        toast.error('❌ Erreur IA');
        return;
      }

      const data = await res.json();
      if (data.response) {
        setReply(data.response);
      } else {
        toast.error('❌ Erreur IA');
      }
    } catch (err) {
      console.error('AI error:', err);
      toast.error('Erreur réseau.');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return <div className={styles.loadingSpinner}></div>;
  if (error) return <div>{error}</div>;

  return (
    <main className={styles.reviewFeedContainer}>
      <h1>Avis Clients</h1>
      {reviews.length === 0 ? (
        <p className={styles.noReviews}>Aucun avis trouvé.</p>
      ) : (
        <div className={styles.reviewList}>
          {reviews.map((review) => (
            <div key={review._id} className={styles.reviewItem}>
              <ReviewCard {...review} />

              {replyingId === review._id ? (
                <div className={styles.replyEditor}>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Écrire une réponse..."
                    rows={4}
                  />
                  <div className={styles.replyButtons}>
                    <button onClick={() => sendReply(review._id)} disabled={!reply}>
                      Envoyer
                    </button>
                    <button onClick={() => setReplyingId(null)}>Annuler</button>
                    <button onClick={() => generateAIReply(review)} disabled={aiLoading}>
                      {aiLoading ? 'Chargement...' : 'Générer avec IA'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {review.response ? (
                    <div className={styles.businessResponse}>
                      <strong>Réponse du propriétaire :</strong>
                      <div>{review.response}</div>
                    </div>
                  ) : (
                    <button onClick={() => openReplyEditor(review)}>Répondre</button>
                  )}
                </>
              )}

              <button
                onClick={() => setConfirmDeleteId(review._id)}
                className={styles.deleteButton}
              >
                Supprimer
              </button>
            </div>
          ))}
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          message="Supprimer cet avis ? Cette action est irréversible."
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </main>
  );
};

export default ReviewFeed;
